import React, { useState } from 'react';
import { Card, Radio, Button, Space, Typography, Divider, Alert, Spin } from 'antd';
import { 
  CreditCardOutlined, 
  BankOutlined, 
  WalletOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {FEATURE_STRIPE} from '../../appConfig';
const { Title, Paragraph } = Typography;

interface PaymentMethodSelectionProps {
  totalAmount: number;
  onSelectCardPayment: () => void;
  onSelectBankTransfer?: () => void;
  onSelectBalancePayment?: () => void;
  balanceInfo?: {
    sufficient: boolean;
    currentBalance: number;
    requiredAmount: number;
  } | null;
  checkingBalance?: boolean;
}

const PaymentMethodSelection: React.FC<PaymentMethodSelectionProps> = ({
  totalAmount,
  onSelectCardPayment,
  onSelectBankTransfer,
  onSelectBalancePayment,
  balanceInfo,
  checkingBalance = false
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'CARD' | 'MANUAL' | 'BALANCE'>('CARD');
  const history = useHistory();
  const { t } = useTranslation();
  const handleContinue = () => {
    if (paymentMethod === 'CARD') {
      onSelectCardPayment();
    } else if (paymentMethod === 'MANUAL' && onSelectBankTransfer) {
      onSelectBankTransfer();
    } else if (paymentMethod === 'MANUAL') {
      history.push('/agency/manual-payment');
    } else if (paymentMethod === 'BALANCE' && onSelectBalancePayment) {
      onSelectBalancePayment();
    }
  };

  const isContinueDisabled = () => {
    if (paymentMethod === 'BALANCE') {
      return checkingBalance || !balanceInfo?.sufficient;
    }
    return false;
  };

  return (
    <Card title={t('payments.choosePaymentMethod')} className="payment-methods-card">
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>{t('payments.totalToPay')}: €{totalAmount?.toFixed(2)}</Title>
      </div>
        <Radio.Group 
        value={paymentMethod} 
        onChange={(e) => setPaymentMethod(e.target.value)} 
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Card 
            size="small" 
            
            className={`payment-method-option ${paymentMethod === 'CARD' ? 'selected' : ''}`}
            onClick={() => setPaymentMethod('CARD')}
          >
            <Radio value="CARD" disabled={!FEATURE_STRIPE}>
              <Space>
                <CreditCardOutlined style={{ fontSize: '18px' }} />
                <span>{t('payments.payByCard')}</span>
              </Space>
            </Radio>
            <Paragraph type="secondary" style={{ marginLeft: 24, marginBottom: 0, fontSize: '12px' }}>
              {t('payments.payByCardDescription')}
            </Paragraph>
          </Card>

          {/* Option de paiement par solde */}
          <Card 
            size="small" 
            className={`payment-method-option ${paymentMethod === 'BALANCE' ? 'selected' : ''}`}
            onClick={() => setPaymentMethod('BALANCE')}
          >
            <Radio value="BALANCE" disabled={checkingBalance || !balanceInfo?.sufficient}>
              <Space>
                <WalletOutlined style={{ fontSize: '18px' }} />
                <span>{t('payments.payWithBalance')}</span>
              </Space>
            </Radio>
            <div style={{ marginLeft: 24, marginTop: 4 }}>
              {checkingBalance ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spin size="small" />
                  <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: '12px' }}>
                    {t('payments.checkingBalance')}
                  </Paragraph>
                </div>
              ) : balanceInfo ? (
                <>
                  <Paragraph type="secondary" style={{ marginBottom: 4, fontSize: '12px' }}>
                    {t('payments.availableBalance')}: {balanceInfo.currentBalance.toFixed(2)}€
                  </Paragraph>
                  {!balanceInfo.sufficient && (
                    <Alert
                      message={t('payments.insufficientBalance', { 
                        amount: (balanceInfo.requiredAmount - balanceInfo.currentBalance).toFixed(2) 
                      })}
                      type="warning"
                      showIcon
                      style={{ fontSize: '11px', marginBottom: 0 }}
                    />
                  )}
                </>
              ) : (
                <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: '12px' }}>
                  {t('payments.payWithBalanceDescription')}
                </Paragraph>
              )}
            </div>
          </Card>
          
          <Card 
            size="small" 
            className={`payment-method-option ${paymentMethod === 'MANUAL' ? 'selected' : ''}`}
            onClick={() => setPaymentMethod('MANUAL')}
          >
            <Radio value="MANUAL">
              <Space>
                <BankOutlined style={{ fontSize: '18px' }} />
                <span>{t('payments.payByCash')}</span>
              </Space>
            </Radio>
            <Paragraph type="secondary" style={{ marginLeft: 24, marginBottom: 0, fontSize: '12px' }}>
              {t('payments.payByCashDescription')}
            </Paragraph>
          </Card>
        </Space>
      </Radio.Group>
      
      <Divider />
        <div style={{ textAlign: 'right' }}>
        <Button 
          type="primary" 
          icon={<ArrowRightOutlined />}
          onClick={handleContinue}
          disabled={isContinueDisabled()}
          loading={checkingBalance && paymentMethod === 'BALANCE'}
        >
          {paymentMethod === 'BALANCE' && !balanceInfo?.sufficient ? t('payments.insufficientBalanceButton') : t('common.continue')}
        </Button>
      </div>
      
      <style>{`
        .payment-method-option {
          border: 1px solid #f0f0f0;
          transition: all 0.3s;
          margin-bottom: 8px;
        }
        
        .payment-method-option.selected {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24,144,255,0.2);
        }
        
        .payment-methods-card .ant-card-body {
          padding: 16px;
        }
      `}</style>
    </Card>
  );
};

export default PaymentMethodSelection;
